import React from 'react';
import { Badge, Box, Tooltip, useTheme } from '@mui/material';
import { PlantIcon } from '@jotpsych/ui/icons/PlantIcon';

import { daysUntil, formatReviewDate, reviewUrgency } from '../utils/reviewDate';

interface PatientTreatmentPlanIndicatorProps {
  isHovered: boolean;
  /** ISO date (YYYY-MM-DD) the plan must be reviewed by. */
  mustBeUpdatedBy?: string | null;
  onClick?: () => void;
}

export const PatientTreatmentPlanIndicator: React.FC<PatientTreatmentPlanIndicatorProps> = ({
  isHovered,
  mustBeUpdatedBy,
  onClick,
}) => {
  const theme = useTheme();

  const days = daysUntil(mustBeUpdatedBy);
  const formatted = formatReviewDate(mustBeUpdatedBy);
  const urgency = reviewUrgency(days);

  const dotColor =
    urgency === 'overdue'
      ? theme.palette.error.main
      : urgency === 'soon'
      ? theme.palette.warning.main
      : undefined;

  const tooltipText = !mustBeUpdatedBy
    ? 'Treatment plan'
    : urgency === 'overdue'
    ? `Review overdue · was due ${formatted}`
    : urgency === 'soon'
    ? `Review due ${formatted} (${days}d)`
    : `Review by ${formatted}`;

  const indicator = (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '16px',
        border: '1.5px solid',
        borderColor: isHovered ? 'borderColors.brand' : 'borderColors.primary',
        bgcolor: isHovered ? 'borderColors.brand' : 'backgroundColors.secondary',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease-in-out',
        transform: isHovered ? 'scale(1.1)' : 'scale(1)',
        '&:hover': {
          bgcolor: 'borderColors.brand',
          borderColor: 'borderColors.brand',
          transform: 'scale(1.1)',
        },
      }}
    >
      <PlantIcon
        sx={{
          fontSize: 18,
          color: isHovered ? 'white' : theme.palette.text.secondary,
          transition: 'color 0.2s ease-in-out',
        }}
      />
    </Box>
  );

  return (
    <Tooltip title={tooltipText} placement="bottom" arrow>
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: onClick ? 'pointer' : 'default',
        }}
      >
        {dotColor ? (
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            variant="dot"
            sx={{
              '& .MuiBadge-dot': {
                bgcolor: dotColor,
                border: `2px solid ${theme.palette.background.paper}`,
                height: 10,
                minWidth: 10,
                borderRadius: '50%',
              },
            }}
          >
            {indicator}
          </Badge>
        ) : (
          indicator
        )}
      </Box>
    </Tooltip>
  );
};
